<script>
/**
 * @component LiquidationSubmissionList
 * @description UC-REG-169 — Listado histórico de submissions (flujo B).
 *              Filtros por prestador/período/estado. Botón "Nueva liquidación"
 *              redirige a `/cuenta-medica/submissions/new`.
 * @example <LiquidationSubmissionList />
 */
import submissionSvc from "@/services/liquidation-submission";
import userSvc from "@/services/user";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";

const DEBOUNCE_MS = 400;
const STATUS_LABELS = {
  draft: "Borrador",
  processing: "Procesando…",
  // UC-REG-196 — 'reviewing' = esperando confirmación del operador.
  reviewing: "En revisión",
  resolved: "Confirmada",
  reverted: "Anulada",
  failed: "Falló",
};
const STATUS_CHIP = {
  draft: "chip--neutral",
  processing: "chip--info",
  reviewing: "chip--warn",
  resolved: "chip--ok",
  reverted: "chip--warn",
  failed: "chip--err",
};
const MONTHS = [
  { v: 1, l: "Enero" },
  { v: 2, l: "Febrero" },
  { v: 3, l: "Marzo" },
  { v: 4, l: "Abril" },
  { v: 5, l: "Mayo" },
  { v: 6, l: "Junio" },
  { v: 7, l: "Julio" },
  { v: 8, l: "Agosto" },
  { v: 9, l: "Septiembre" },
  { v: 10, l: "Octubre" },
  { v: 11, l: "Noviembre" },
  { v: 12, l: "Diciembre" },
];

export default {
  name: "LiquidationSubmissionList",
  components: { Spinner },
  data() {
    return {
      docs: [],
      total: 0,
      isLoading: false,
      offset: 0,
      limit: 20,
      // filtros
      filterProvider: "",
      filterYear: new Date().getFullYear(),
      filterMonth: "",
      filterStatus: "",
      providerOptions: [],
      providerSearchLoading: false,
      months: MONTHS,
      years: this.buildYears(),
    };
  },
  computed: {
    statusLabels() {
      return STATUS_LABELS;
    },
    statusChip() {
      return STATUS_CHIP;
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    /** @returns {number[]} últimos 5 años + año actual */
    buildYears() {
      const y = new Date().getFullYear();
      return [y, y - 1, y - 2, y - 3, y - 4];
    },
    /** @method fetch - Carga listado con filtros aplicados */
    async fetch() {
      this.isLoading = true;
      try {
        const params = { offset: this.offset, limit: this.limit };
        if (this.filterProvider) params.provider = this.filterProvider;
        if (this.filterYear) params.year = this.filterYear;
        if (this.filterMonth) params.month = this.filterMonth;
        if (this.filterStatus) params.status = this.filterStatus;
        const res = await submissionSvc.list(params);
        this.docs = res.docs || [];
        this.total = res.total || 0;
      } catch (err) {
        this.$message.error("Error al cargar liquidaciones");
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    /** @method searchProviders - Users con rol Doctor (donde apuntan los contratos) */
    searchProviders: debounce(async function (query) {
      this.providerSearchLoading = true;
      try {
        const res = await userSvc.getDoctors({
          searchInput: query || "",
          limit: 20,
        });
        this.providerOptions = (res.docs || []).map((u) => ({
          value: u._id,
          label:
            `${u.lastName || ""}, ${u.firstName || ""}`
              .replace(/^, /, "")
              .trim() ||
            u.username ||
            "—",
        }));
      } finally {
        this.providerSearchLoading = false;
      }
    }, DEBOUNCE_MS),
    /** @method formatDate - dd/mm/yy hh:mm en AR */
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    /** @method formatMoney - Format $XX.XXX */
    formatMoney(n) {
      if (n == null) return "-";
      return `$${Number(n).toLocaleString("es-AR")}`;
    },

    /**
     * @description UC-REG-201 — Label del chip Factura según estado + match.
     * @param {{state:string,matchStatus?:string}} inv
     * @returns {string}
     */
    invoiceLabel(inv) {
      if (!inv || inv.state === "none") return "Sin factura";
      if (inv.state === "paid") return "Pagada";
      if (inv.state === "sent_for_payment") return "Enviada a pagar";
      if (inv.matchStatus === "mismatch") return "Con diferencia";
      return "Facturada";
    },

    /**
     * @description UC-REG-201 — Clase CSS del chip Factura.
     * @param {{state:string,matchStatus?:string}} inv
     * @returns {string}
     */
    invoiceChipClass(inv) {
      if (!inv || inv.state === "none") return "chip--neutral";
      if (inv.state === "paid") return "chip--paid";
      if (inv.state === "sent_for_payment") return "chip--sent";
      if (inv.matchStatus === "mismatch") return "chip--err";
      return "chip--info";
    },
    /** @method goToNew - Ir al form de creación */
    goToNew() {
      this.$router.push({ name: "liquidation-submission-new" });
    },
    /** @method goToDetail - Ver detalle del submission */
    goToDetail(row) {
      this.$router.push({
        name: "liquidation-submission-detail",
        params: { id: row._id },
      });
    },
    /** @method onPageChange */
    onPageChange(page) {
      this.offset = (page - 1) * this.limit;
      this.fetch();
    },
    /** @method onFilterChange */
    onFilterChange() {
      this.offset = 0;
      this.fetch();
    },
  },
};
</script>

<template lang="pug">
.subm-list
  //- Header con filtros
  .subm-list__toolbar
    el-select(
      v-model="filterProvider"
      filterable remote reserve-keyword clearable
      placeholder="Todos los prestadores"
      :remote-method="searchProviders"
      :loading="providerSearchLoading"
      @change="onFilterChange"
      @visible-change="v => v && !providerOptions.length && searchProviders('')"
      style="width:240px"
    )
      el-option(v-for="o in providerOptions" :key="o.value" :label="o.label" :value="o.value")
    el-select(v-model="filterYear" placeholder="Año" @change="onFilterChange" style="width:110px")
      el-option(v-for="y in years" :key="y" :label="String(y)" :value="y")
    el-select(v-model="filterMonth" placeholder="Mes" clearable @change="onFilterChange" style="width:140px")
      el-option(v-for="m in months" :key="m.v" :label="m.l" :value="m.v")
    el-select(v-model="filterStatus" placeholder="Todos los estados" clearable @change="onFilterChange" style="width:170px")
      el-option(v-for="(l, k) in statusLabels" :key="k" :label="l" :value="k")
    .subm-list__spacer
    el-button(type="primary" @click="goToNew" size="small") + Nueva liquidación

  //- Tabla
  .subm-list__wrap(v-loading="isLoading")
    table.subm-list__table
      thead
        tr
          //- UC-REG-199 — Número de operación (LIQ-NNNNNN) como primera columna
          th LIQ
          th Fecha
          th Prestador
          th Período
          th Origen
          th.amt Líneas
          th.amt Aceptado
          th.amt Débito
          th Estado
          //- UC-REG-201 — Factura (chip + clip descarga)
          th Factura
          th.acts
      tbody
        tr(v-if="!isLoading && docs.length === 0")
          td.empty(colspan="11") Sin liquidaciones para los filtros aplicados
        tr(
          v-for="d in docs"
          :key="d._id"
          @click="goToDetail(d)"
        )
          td.nowrap
            span.op-code {{ d.operationCode || '—' }}
          td.nowrap {{ formatDate(d.submittedAt) }}
          td
            span.provider {{ (d.providerSnapshot && (d.providerSnapshot.lastName + ', ' + d.providerSnapshot.firstName)) || '—' }}
            //- UC-REG-204 — Chip con el número de prestador (PR-NNNNNN)
            span.pr-code(v-if="d.providerSnapshot && d.providerSnapshot.providerCode") {{ d.providerSnapshot.providerCode }}
          td.nowrap {{ String(d.billingPeriod.month).padStart(2,'0') }}/{{ d.billingPeriod.year }}
          td
            span.chip.chip--info(v-if="d.sourceMode === 'imported'") Importado
            span.chip.chip--neutral(v-else) Manual
          td.amt {{ (d.totals && d.totals.linesTotal) || 0 }}
          td.amt
            span.amt--ok(v-if="d.totals && d.totals.acceptedAmount") {{ formatMoney(d.totals.acceptedAmount) }}
            span.muted(v-else) —
          td.amt
            span.amt--err(v-if="d.totals && d.totals.debitAmount") {{ formatMoney(d.totals.debitAmount) }}
            span.muted(v-else) —
          td
            span.chip(:class="statusChip[d.status] || 'chip--neutral'") {{ statusLabels[d.status] || d.status }}
          //- UC-REG-201 — Factura + adjunto
          td.nowrap
            template(v-if="d.invoiceSummary")
              span.chip(:class="invoiceChipClass(d.invoiceSummary)") {{ invoiceLabel(d.invoiceSummary) }}
              a.invoice-clip(
                v-if="d.invoiceSummary.attachmentUrl"
                :href="d.invoiceSummary.attachmentUrl"
                target="_blank"
                rel="noopener"
                :title="'Descargar factura ' + (d.invoiceSummary.invoiceNumber || '')"
                @click.stop
              )
                micon(name="attach_file")
          td.acts
            el-button(size="mini" @click.stop="goToDetail(d)") Ver

    .subm-list__pager(v-if="total > limit")
      el-pagination(
        layout="prev, pager, next, total"
        :total="total"
        :page-size="limit"
        :current-page="Math.floor(offset / limit) + 1"
        @current-change="onPageChange"
      )
</template>

<style lang="scss" scoped>
.subm-list {
  font-family: "Plus Jakarta Sans", sans-serif;
}
.subm-list__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.subm-list__spacer {
  flex: 1;
}

.subm-list__wrap {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.subm-list__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.subm-list__table th {
  text-align: left;
  padding: 10px 14px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  font-weight: 500;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.subm-list__table th.amt {
  text-align: right;
}
.subm-list__table th.acts {
  width: 80px;
}
.subm-list__table td {
  padding: 12px 14px;
  border-bottom: 0.5px solid #f1f5f9;
  color: #1a1f36;
  vertical-align: middle;
}
.subm-list__table tbody tr {
  cursor: pointer;
  &:hover {
    background: #f8fafc;
  }
}
.subm-list__table td.amt {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.subm-list__table td.acts {
  text-align: right;
}
.subm-list__table td.empty {
  text-align: center;
  color: #94a3b8;
  padding: 40px;
}
.subm-list__table td.nowrap {
  white-space: nowrap;
}

.provider {
  font-weight: 500;
  color: #1a1f36;
}
/* UC-REG-199 — LIQ-NNNNNN */
.op-code {
  font-family: monospace;
  font-weight: 700;
  color: #1e3a8a;
  font-size: 12.5px;
}
/* UC-REG-204 — chip PR-NNNNNN al lado del nombre del prestador */
.pr-code {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #1e3a8a;
  font-family: monospace;
  font-size: 11px;
  font-weight: 600;
}
.muted {
  color: #94a3b8;
  margin: 0 4px;
}
.amt--ok {
  color: #065f46;
  font-weight: 500;
}
.amt--err {
  color: #b91c1c;
}

.chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
}
.chip--ok {
  background: #d1fae5;
  color: #065f46;
}
.chip--warn {
  background: #fef3c7;
  color: #92400e;
}
.chip--err {
  background: #fee2e2;
  color: #b91c1c;
}
.chip--info {
  background: #dbeafe;
  color: #1e40af;
}
.chip--neutral {
  background: #f1f5f9;
  color: #475569;
}
/* UC-REG-201 — Chips estado factura */
.chip--sent {
  background: #ede9fe;
  color: #5b21b6;
}
.chip--paid {
  background: #d1fae5;
  color: #065f46;
}

/* UC-REG-201 — Clip descarga PDF factura */
.invoice-clip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 6px;
  border-radius: 6px;
  color: #1e3a8a;
  vertical-align: middle;
  transition: background 120ms;
}
.invoice-clip:hover {
  background: #eef2ff;
}
.invoice-clip ::v-deep svg {
  width: 14px;
  height: 14px;
}

.subm-list__pager {
  padding: 12px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}
</style>
